import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '@/shared/ui/Card';
import { Button } from '@/shared/ui/Button';
import { EmptyState } from '@/shared/ui/EmptyState';
import { SkeletonList } from '@/shared/ui/Skeleton';
import { useToast } from '@/shared/ui/Toast';
import { listMeetings, listParticipants } from '@/shared/api/meetings';
import { completeTransfer, getSettlement } from '@/shared/api/settlement';
import { useAuthStore } from '@/shared/auth/store';
import { formatKRW } from '@/shared/lib/money';

/**
 * 입금 알림 원탭 제안 진입점 (Android 래퍼 딥링크 대상).
 * /transfer-suggest?amount=15000&from=김성찬
 * 감지된 입금 금액과 일치하는 "내가 받을 미완료 송금"을 찾아
 * 사용자가 확인 후 완료로 표시한다. 자동 처리는 하지 않는다.
 */
export default function TransferSuggestPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const user = useAuthStore((s) => s.user);
  const [searchParams] = useSearchParams();
  const amountParam = searchParams.get('amount') ?? '';
  const fromName = searchParams.get('from') ?? '';
  const amount = /^\d+$/.test(amountParam) ? Number(amountParam) : null;

  const normalizedFromName = fromName.trim().toLowerCase();
  const meetingsQ = useQuery({ queryKey: ['meetings'], queryFn: listMeetings });
  // 정산은 모임 상태와 무관하게 존재할 수 있다(투표 중에도 선결제 정산 가능).
  // 모든 모임의 정산을 조회하고, 정산이 없는 모임(404)은 조용히 건너뛴다.
  const candidates = meetingsQ.data ?? [];

  const settlementQs = useQueries({
    queries: candidates.map((m) => ({
      queryKey: ['settlement', m.id],
      queryFn: () => getSettlement(m.id),
      retry: false,
    })),
  });
  const participantQs = useQueries({
    queries: candidates.map((m) => ({
      queryKey: ['participants', m.id],
      queryFn: () => listParticipants(m.id),
    })),
  });

  const completeMut = useMutation({
    mutationFn: (transferId: string) => completeTransfer(transferId, true),
    onSuccess: (_, transferId) => {
      const hit = matches.find((c) => c.transferId === transferId);
      qc.invalidateQueries({ queryKey: ['settlement'] });
      toast.success('송금 완료로 표시했어요.');
      if (hit) navigate(`/meetings/${hit.meetingId}/settlement`, { replace: true });
    },
    onError: () => toast.error('완료 표시에 실패했어요.'),
  });

  if (amount == null) {
    return <EmptyState title="잘못된 링크" description="입금 금액 정보가 없어요." />;
  }
  const loading = meetingsQ.isLoading || settlementQs.some((q) => q.isLoading);
  if (loading) return <SkeletonList count={2} />;

  // 모든 모임의 정산에서 "내가 받을, 같은 금액의, 미완료" 송금을 모은다
  const matches = candidates.flatMap((m, i) => {
    const s = settlementQs[i]?.data;
    const parts = participantQs[i]?.data;
    if (!s) return [];
    return s.transfers
      .filter((t) => t.toUserId === user?.id && t.status === 'PENDING' && t.amount === amount)
      .map((t) => ({
        transferId: t.id,
        meetingId: m.id,
        meetingTitle: m.title,
        fromNickname: parts?.find((p) => p.userId === t.fromUserId)?.nickname ?? '멤버',
      }));
  });
  const nameMatched = normalizedFromName
    ? matches.filter((m) => m.fromNickname.trim().toLowerCase().includes(normalizedFromName))
    : matches;
  const visibleMatches = nameMatched.length > 0 ? nameMatched : matches;
  const needsManualCheck = matches.length > 1 || (normalizedFromName.length > 0 && nameMatched.length === 0);

  return (
    <section className="space-y-5 pt-2">
      <header>
        <span className="inline-block h-0.5 w-8 bg-primary" aria-hidden="true" />
        <p className="mt-3 text-micro font-semibold uppercase tracking-wide-micro text-ink-muted">
          입금 감지
        </p>
        <h1 className="mt-1.5 text-section font-semibold tracking-tight-feature text-ink">
          {formatKRW(amount)} 받으셨나요?
        </h1>
        <p className="mt-1 text-body text-ink-muted">
          {fromName ? `${fromName}님에게서 받은 입금이라면 ` : '정산으로 받은 돈이라면 '}
          아래에서 완료로 표시해주세요.
        </p>
      </header>

      {matches.length === 0 ? (
        <EmptyState
          title="일치하는 정산 내역이 없어요"
          description="같은 금액의 받을 송금이 없거나 이미 완료됐어요."
        />
      ) : (
        <ul className="space-y-3">
          {needsManualCheck ? (
            <li className="rounded-2xl border border-warning-soft bg-warning-soft/45 px-3 py-2 text-small font-semibold text-warning">
              같은 금액 후보가 여러 개예요. 보낸 사람과 모임명을 확인한 뒤 완료 표시하세요.
            </li>
          ) : null}
          {visibleMatches.map((c) => (
            <li key={c.transferId}>
              <Card>
                <p className="text-card font-semibold tracking-tight-heading text-ink">
                  {c.meetingTitle}
                </p>
                <p className="mt-1 text-body text-ink-muted">
                  {c.fromNickname} → 나 · {formatKRW(amount)}
                </p>
                <Button
                  variant="brand"
                  className="mt-4 w-full"
                  disabled={completeMut.isPending}
                  onClick={() => completeMut.mutate(c.transferId)}
                >
                  받았어요 — 완료로 표시
                </Button>
              </Card>
            </li>
          ))}
        </ul>
      )}

      <Button variant="ghost" className="w-full" onClick={() => navigate('/home')}>
        홈으로
      </Button>
    </section>
  );
}
